import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import BatchJobService from "src/services/batchJobService";
import commonService from "src/services/commonService";
import ExtractionService, { BatchExtractionPayload } from "src/services/extractionService";
import { LOADING_STATE } from "src/types/constants";
import { Model, setSelectedModelId } from "src/store/modelManagementSlice";

export enum BatchJobStatus {
  Processing = "Processing",
  Completed = "Completed",
  Error = "Error",
}

export type BatchExtractionJobView = {
  id: string;
  s3OutputFilePath: string;
  inputFileName: string;
  modelName: string;
  status: string;
  processingTimeInSeconds: string;
  creationTimestamp: string;
  ownerAlias: string;
  linesProcessed: number;
  linesFailed: number;
  totalLines: number;
};

export type BatchExtractionJob = {
  id: string;
  s3OutputFilePath: string;
  inputFileName: string;
  modelId: string;
  model: Model;
  status: string;
  processingTimeInSeconds: string;
  creationTimestamp: string;
  ownerAlias: string;
  linesProcessed: number;
  linesFailed: number;
  totalLines: number;
};

export type BatchDataExtractionPageState = {
  uploadFile: File | undefined;
  generateLLMClarification: boolean;
  batchExtractionLoading: LOADING_STATE;
  batchJobs: BatchExtractionJobView[];
  selectedBatchJob: BatchExtractionJobView;
  newCreatedBatchJobId: string;
  batchExtractionErrorMessage: string;
  showBatchErrorMessageBanner: boolean;
};

const initialBatchJob = {
  id: "",
  s3OutputFilePath: "",
  inputFileName: "",
  modelId: "",
  modelName: "",
  status: "",
  processingTimeInSeconds: "",
  creationTimestamp: "",
  ownerAlias: "",
  linesProcessed: 0,
  linesFailed: 0,
  accuracy: {},
  totalLines: 0,
};

const initialState: BatchDataExtractionPageState = {
  uploadFile: undefined,
  generateLLMClarification: false,
  batchExtractionLoading: "idle",
  batchJobs: [],
  selectedBatchJob: initialBatchJob,
  newCreatedBatchJobId: "",
  batchExtractionErrorMessage: "",
  showBatchErrorMessageBanner: false,
};

export const BATCH_PROCESS_URL = "/extractions/batch";

export const batchExtraction = createAsyncThunk(
  "batchExtraction",
  async (payload: BatchExtractionPayload) => {
    const { data } = await ExtractionService.batchExtraction(payload);
    return data;
  },
);

export const getBatchJobs = createAsyncThunk("getBatchJobs", async () => {
  const { data } = await BatchJobService.getBatchJobs();
  return data;
});

export const getBatchJobById = createAsyncThunk(
  "getBatchJobById",
  async (jobId: string, { dispatch }) => {
    const { data } = await BatchJobService.getBatchJobById(jobId);
    if (data.model?.id) {
      dispatch(setSelectedModelId(data.model.id));
    }
    return data;
  },
);

export const getPreSignUrlAndDownload = createAsyncThunk(
  "executeDownloadWorkflow",
  commonService.getPreSignUrlAndDownload,
);

/** Data Extraction Page Slice */
const { reducer, actions } = createSlice({
  name: "batchExtractionSlice",
  initialState,
  reducers: {
    setUploadFile: (state, action) => {
      state.uploadFile = action.payload;
    },
    resetBatchExtractionApi: (state) => {
      state.batchExtractionLoading = "idle";
      state.batchExtractionErrorMessage = "";
      state.showBatchErrorMessageBanner = false;
    },
    setGenerateLLMClarification: (state, action) => {
      state.generateLLMClarification = action.payload;
    },
    setShowBatchErrorMessageBanner: (state, action) => {
      state.showBatchErrorMessageBanner = true;
    },
    setSelectedBatchJob: (state, action) => {
      state.selectedBatchJob = action.payload;
    },
    resetNewCreatedBatchJobId: (state) => {
      state.newCreatedBatchJobId = "";
    },
  },
  extraReducers: (builder) => {
    builder.addCase(batchExtraction.pending, (state) => {
      state.batchExtractionLoading = "pending";
    });
    builder.addCase(batchExtraction.fulfilled, (state, { payload }) => {
      state.batchExtractionLoading = "fulfilled";
      state.newCreatedBatchJobId = payload.id;
    });
    builder.addCase(batchExtraction.rejected, (state, { payload }) => {
      state.batchExtractionLoading = "rejected";
    });
    builder.addCase(getBatchJobs.fulfilled, (state, { payload }) => {
      state.batchJobs = payload;
      state.batchJobs.sort(
        (a: BatchExtractionJobView, b: BatchExtractionJobView) =>
          new Date(b.creationTimestamp).valueOf() - new Date(a.creationTimestamp).valueOf(),
      );
    });
    builder.addCase(getBatchJobById.fulfilled, (state, { payload }) => {
      state.selectedBatchJob = { ...payload };
    });
  },
});

export const {
  setUploadFile,
  resetBatchExtractionApi,
  resetNewCreatedBatchJobId,
  setShowBatchErrorMessageBanner,
} = actions;

export default reducer;
